import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, gql } from '@apollo/client';
import SalesOrderPickScan from './SalesOrderPickScan';
import ApiError from '@/lib/shared-coms/ApiError';
import { Spinner } from 'equasion-lib';
import { BULK_INVENTORY_EVENT_CREATE } from '@/lib/graphql/fragments';
import { useRootStore } from '@/lib/providers/RootProvider';

const CLIENT_ORDER = gql`
  query salesOrder($id: ID!, $status: String!, $vendorId: ID!) {
    salesOrder(id: $id, vendorId: $vendorId) {
      id
      vendorSalesOrderNumber
      vendorStatus
      orderItems {
        id
        productId
        fdaProductId
        quantity
        isShortDated
        # The line's second pick rule. Selected alongside isShortDated because the scan header
        # badges both, and because a picker who is about to be refused mid-scan needs the reason
        # visible before the trigger is pulled, not only in the alert afterwards.
        isChargeback
        serializedItemOfStatusCount(status: $status)
        product {
          id
          title
          fdaProduct {
            id
            name
            elevenDigitNdc
            deaSchedule
          }
          upcProduct {
            id
          }
        }
      }
    }
  }
`;

const GET_ZONES_WITH_PRODUCT = gql`
  query ZonesWithProduct(
    $companyId: ID!
    $fdaProductId: ID
    $upcProductId: ID
    $salesOrderId: ID
  ) {
    zonesWithProduct(
      companyId: $companyId
      fdaProductId: $fdaProductId
      upcProductId: $upcProductId
      salesOrderId: $salesOrderId
    ) {
      id
      name
      zoneType
      shortDatedQuantity
      pickSequence
    }
  }
`;

const SalesOrderPickScanData = (props) => {
  const vendorId = useRootStore((state) => state.selectedCompany.id);
  const [searchParams] = useSearchParams();
  const salesOrderId = searchParams.get('salesOrderId');
  const salesOrderItemId = searchParams.get('salesOrderItemId');
  // The zone the picking screen's walk sent the picker to, if any.
  const suggestedZoneId = searchParams.get('zoneId') || null;
  const [bulkInventoryEventCreate] = useMutation(BULK_INVENTORY_EVENT_CREATE);

  const { data, error, loading, refetch } = useQuery(CLIENT_ORDER, {
    variables: { id: salesOrderId, vendorId, status: 'PICKED' },
    fetchPolicy: 'network-only'
  });

  const { salesOrder } = data || {};
  const orderItem = salesOrder?.orderItems?.find(
    ({ id }) => id === salesOrderItemId
  );
  const fdaProductId = orderItem?.product?.fdaProduct?.id;

  const {
    data: zonesData,
    error: zonesError,
    loading: zonesLoading,
    refetch: refetchZones
  } = useQuery(GET_ZONES_WITH_PRODUCT, {
    variables: {
      companyId: vendorId,
      fdaProductId,
      upcProductId: null,
      salesOrderId
    },
    skip: !fdaProductId,
    fetchPolicy: 'network-only'
  });

  // The order data and the pickable-zones list live in two separate queries, so
  // a pick must refresh both — otherwise a zone emptied by the pick would linger
  // in the zone list until a page refresh. Guard the zones refetch with the same
  // condition as the query's `skip` so we don't fire it when it was never active.
  const refetchAll = async () => {
    await Promise.all([refetch(), fdaProductId ? refetchZones() : Promise.resolve()]);
  };

  if (loading) return <Spinner />;
  if (error) return <ApiError error={error} />;
  if (zonesError) return <ApiError error={zonesError} />;
  if (zonesLoading) return <Spinner />;

  const zonesWithProduct = zonesData?.zonesWithProduct || [];

  return (
    <SalesOrderPickScan
      bulkInventoryEventCreate={bulkInventoryEventCreate}
      salesOrder={salesOrder}
      orderItem={orderItem}
      refetch={refetchAll}
      zonesWithProduct={zonesWithProduct}
      suggestedZoneId={suggestedZoneId}
      {...props}
    />
  );
};

export default SalesOrderPickScanData;
